import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { find, propEq, values } from 'ramda';
import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import HeadingText from '@ui/atoms/HeadingText';
import TextButton from '@ui/atoms/TextButton';

import PasswordInput from '@ui/forms/PasswordInput';
import TextInput from '@ui/forms/TextInput';

import { useTracking } from '@hooks/useTracking';

import { AuthStep, AuthStepContext, useAuth } from '@providers/AuthProvider';

import { SpacingTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

import SignInNavigation from '../SignInNavigation';
import awsAuthErrorMessages, { AwsAuthErrorMessageValue } from '../awsAuthErrorMessages';

const SpacedWrapper = styled.div<{
	gap: SpacingTypes;
	marginBottom: SpacingTypes;
}>`
	gap: ${({ gap }) => theme.spacing[gap]};
	margin-bottom: ${({ marginBottom }) => theme.spacing[marginBottom]};
`;

interface ISignInFormProps {
	setAuthStep: (authStep: AuthStepContext) => void;
	onComplete: () => void;
	previousPage?: string;
	setCredentials: Dispatch<SetStateAction<Partial<{ email: string; password: string }>>>;
}

const SignInForm: FC<ISignInFormProps> = ({
	setAuthStep,
	onComplete,
	previousPage,
	setCredentials,
}) => {
	const { signIn } = useAuth();
	const { trackEvent } = useTracking('login-form-viewed');
	const location = useLocation();

	const [submitting, setSubmitting] = useState(false);

	const schema = z.object({
		email: z.string().min(1, { message: 'A valid email address is required.' }).email({
			message: "Your email address doesn't look like an email address. Please check again!",
		}),
		password: z.string().min(1, { message: 'A valid password is required.' }),
	});

	type Schema = Partial<z.input<typeof schema>>;

	const {
		control,
		handleSubmit,
		register,
		setError,
		formState: { errors },
	} = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
	});

	const onSubmit = async (data: Schema) => {
		try {
			setSubmitting(true);
			setCredentials(data);
			const newAuthStep = await signIn(data.email ?? '', data.password ?? '');

			console.log('new', newAuthStep);
			if (newAuthStep.nextStep === AuthStep.done) {
				onComplete();
			}

			setAuthStep(newAuthStep as AuthStepContext);
		} catch (e) {
			console.log(e);
			const errorMessage: AwsAuthErrorMessageValue | undefined = find<AwsAuthErrorMessageValue>(
				propEq('awsErrorMessage', e?.message ?? ''),
				values(awsAuthErrorMessages)
			);

			setError('password', {
				type: 'manual',
				message:
					errorMessage?.userErrorMessage ?? awsAuthErrorMessages.invalidSignIn.userErrorMessage,
			});
			trackEvent('login-failed');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<SpacedWrapper gap="sm" marginBottom="lg">
				<HeadingText>
					Welcome back! <br />
					Sign in to continue
				</HeadingText>
			</SpacedWrapper>
			<SpacedWrapper gap="sm" marginBottom="sm">
				<TextInput
					type="email"
					placeholder="Email address"
					label="Email address"
					fullWidth
					errorLabel={errors.email?.message}
					{...register('email')}
				/>
			</SpacedWrapper>
			<SpacedWrapper gap="sm" marginBottom="sm">
				<Controller
					name="password"
					control={control}
					render={({ field }) => (
						<PasswordInput
							placeholder="Password"
							label="Password"
							errorLabel={errors.password?.message}
							{...field}
						/>
					)}
				/>
			</SpacedWrapper>
			<SpacedWrapper gap="sm" marginBottom="xl">
				<TextButton
					variant="text"
					underline
					label="Reset password"
					onClick={() =>
						navigate('/reset_password', {
							state: location.state as object | undefined,
						})
					}
				/>
			</SpacedWrapper>
			<SignInNavigation submitting={submitting} previousPage={previousPage} />
		</form>
	);
};

export default SignInForm;
